import { createFileRoute, Link } from "@tanstack/react-router";
import { pageSchemaScript, PERSON_ID } from "@/seo/schema";
import { PageShell } from "@/components/PageShell";
import { SectionHeader } from "@/components/SectionHeader";
import { Reveal } from "@/components/Reveal";
import { ArrowRight, Mic2, MapPin, Volume2, Layers, Presentation, Video } from "lucide-react";
import solAuditorium from "@/assets/sol-auditorium.jpg";

import { ogUrl } from "@/lib/ogSections";

const SITE_ORIGIN = "https://arifkhanglobal.com";
const URL = `${SITE_ORIGIN}/locations/auditorium-av-consultant-delhi-ncr`;
const TITLE = "Auditorium AV Consultant in Delhi NCR | Arif Khan";
const DESCRIPTION =
  "Independent auditorium AV consultant in Delhi NCR — acoustic-first halls, hybrid lecture capture, one-touch operation and stage AV for institutions.";


const WHO_FOR = [
  "Educational institutions in Delhi, Gurugram, Noida and Greater Noida planning a new auditorium or upgrading an existing one",
  "Corporate campuses across NCR planning a town-hall, training auditorium or event venue",
  "Cultural and convention venues seeking acoustic-first design and hybrid capability",
  "Architects coordinating an auditorium AV brief with civil, stage and HVAC scope",
];

const PLAN = [
  { Icon: Layers, title: "Acoustic shell first", desc: "Room modelling, treatment plan and STI targets defined before any loudspeaker is specified." },
  { Icon: Volume2, title: "Loudspeaker design", desc: "Line-array or point-source designed from coverage, not from a brand catalogue, with subs placed to room modes." },
  { Icon: Video, title: "Hybrid capture & streaming", desc: "Multi-camera auto-tracking, LMS integration and live streaming planned at design — not added later." },
  { Icon: Presentation, title: "One-touch operation", desc: "Single touch-panel UX so the room runs without a full-time technician, with presets per session type." },
];

const MISTAKES = [
  "Loudspeakers and acoustics treated as a post-fit-out purchase",
  "Stage lighting and AV procured without operator workflow in mind",
  "Hybrid capture and streaming added after commissioning",
  "Control room designed without sightlines to stage or screen",
  "No documented operator SOP for routine session types",
];

const FAQS = [
  { q: "How early should an auditorium AV consultant be involved?", a: "At architectural design, before column grids, ceiling heights and HVAC routing are frozen. Auditorium acoustics are decided by the shell — not by the speakers." },
  { q: "Do you cover stage lighting and rigging as well as AV?", a: "Yes. Stage lighting, rigging, audio, projection, capture and house lights are designed together so a single operator can run any session type." },
  { q: "Can you integrate lecture capture with the institution's LMS?", a: "Yes — multi-camera auto-tracking with recording and live streaming, integrated with common LMS platforms used in NCR institutions." },
  { q: "What STI targets do you design to?", a: "Speech Transmission Index ≥ 0.60 across all seats for lecture and conference halls, with higher targets for performance venues — verified on commissioning, not estimated on paper." },
];

const RELATED = [
  { to: "/solutions/auditorium-av", label: "Auditorium AV solution" },
  { to: "/industries/education-auditoriums", label: "Education & Auditoriums" },
  { to: "/ask-arif/auditorium-av-planning-guide", label: "Auditorium AV planning guide" },
  { to: "/for-architects", label: "For Architects — design-stage coordination" },
  { to: "/solutions/enterprise-av", label: "Enterprise AV (boardrooms & town-halls)" },
  { to: "/contact", label: "Let's Talk" },
] as const;

export const Route = createFileRoute("/locations/auditorium-av-consultant-delhi-ncr")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
      { property: "og:image", content: ogUrl(solAuditorium) },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: ogUrl(solAuditorium) },
      { name: "geo.region", content: "IN-DL" },
      { name: "geo.placename", content: "Delhi NCR" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      pageSchemaScript(
        {
          path: "/locations/auditorium-av-consultant-delhi-ncr",
          type: "WebPage",
          name: TITLE,
          description: DESCRIPTION,
          breadcrumb: [{ name: "Locations", url: "/locations" }, { name: "Auditorium AV Consultant in Delhi NCR" }],
          extra: { about: { "@id": `${URL}#service` }, mainEntity: { "@id": `${URL}#service` } },
        },
        [
          {
            "@type": "Service",
            "@id": `${URL}#service`,
            name: "Auditorium AV Consultant — Delhi NCR",
            serviceType: "Auditorium Acoustic & Audio Visual Consultancy",
            provider: { "@id": PERSON_ID },
            areaServed: [{ "@type": "City", name: "New Delhi" }, { "@type": "City", name: "Gurugram" }, { "@type": "City", name: "Noida" }, { "@type": "City", name: "Greater Noida" }, { "@type": "City", name: "Faridabad" }],
            url: URL,
            description: DESCRIPTION,
          },
          {
            "@type": "FAQPage",
            "@id": `${URL}#faq`,
            url: URL,
            isPartOf: { "@id": `${URL}#webpage` },
            mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
          },
        ],
      ),
    ],
  }),
});

function Page() {
  return (
    <PageShell>
      <section className="relative pt-40 pb-24 border-b border-border bg-surface overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div aria-hidden className="absolute inset-0 opacity-50" style={{ backgroundImage: `url(${solAuditorium})`, backgroundSize: "cover", backgroundPosition: "center", filter: "blur(40px) saturate(1.1)", transform: "scale(1.15)" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/85 to-surface/60" />
        <div className="container-page relative">
          <div className="flex items-center gap-2 text-ink-soft">
            <MapPin className="h-4 w-4" />
            <span className="eyebrow">Delhi NCR · Locations</span>
          </div>
          <h1 className="mt-6 text-balance text-5xl md:text-7xl font-semibold tracking-tight leading-[0.95] max-w-4xl">
            Auditorium AV Consultant in Delhi&nbsp;NCR
          </h1>
          <p className="mt-6 max-w-2xl text-xl text-ink-soft leading-relaxed">
            Independent, acoustic-first auditorium design for institutions, corporates and cultural venues across Delhi, Gurugram, Noida, Greater Noida and Faridabad. Speech-intelligible halls, hybrid capture, one-touch operation.
          </p>
        </div>
      </section>

      <section className="py-24">
        <div className="container-page grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5"><SectionHeader eyebrow="Who this is for" title="Institutions and design teams in NCR." /></div>
          <div className="lg:col-span-7 grid gap-4">
            {WHO_FOR.map((w, i) => (
              <Reveal key={w} delay={i * 0.04}>
                <div className="flex items-start gap-4 rounded-xl border border-border bg-surface p-5">
                  <div className="font-mono text-xs text-ink-soft w-8 pt-0.5">0{i + 1}</div>
                  <div className="text-sm text-foreground leading-relaxed">{w}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-surface border-y border-border">
        <div className="container-page">
          <SectionHeader eyebrow="What we plan" title="From acoustic shell to one-touch operation." description="Decisions sequenced before the room is poured — so the speakers, screens and capture all work together on day one." />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PLAN.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.05}>
                <div className="h-full rounded-2xl border border-border bg-background p-6">
                  <p.Icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  <h3 className="mt-6 text-lg font-semibold tracking-tight">{p.title}</h3>
                  <p className="mt-3 text-sm text-ink-soft leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="container-page grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5"><SectionHeader eyebrow="Common mistakes" title="What we see go wrong in NCR auditoriums." /></div>
          <div className="lg:col-span-7 grid gap-4">
            {MISTAKES.map((m, i) => (
              <Reveal key={m} delay={i * 0.04}>
                <div className="flex items-start gap-4 rounded-xl border border-border bg-surface p-5">
                  <div className="font-mono text-xs text-ink-soft w-8 pt-0.5">{String(i + 1).padStart(2, "0")}</div>
                  <div className="text-sm text-foreground leading-relaxed">{m}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-surface border-y border-border">
        <div className="container-page grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5"><SectionHeader eyebrow="Related services" title="Where this connects." /></div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {RELATED.map((r) => (
              <Link key={r.to} to={r.to as "/"} className="group flex items-center justify-between rounded-xl border border-border bg-background p-5 hover:border-border-strong hover:shadow-card transition-all">
                <span className="text-sm font-medium">{r.label}</span>
                <ArrowRight className="h-4 w-4 text-ink-soft group-hover:text-primary transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="container-page grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5"><SectionHeader eyebrow="FAQ" title="Questions we hear from NCR institutions." /></div>
          <div className="lg:col-span-7 grid gap-4">
            {FAQS.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.05}>
                <details className="group rounded-xl border border-border bg-background p-5 open:border-border-strong open:shadow-card transition-all">
                  <summary className="flex cursor-pointer list-none items-start justify-between gap-4 font-medium">
                    <span>{f.q}</span>
                    <Mic2 className="h-4 w-4 mt-1 text-ink-soft group-open:text-primary transition-colors" />
                  </summary>
                  <p className="mt-3 text-sm text-ink-soft leading-relaxed">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 border-t border-border bg-surface">
        <div className="container-page flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight leading-tight max-w-2xl text-balance">Planning an auditorium in Delhi NCR?</h2>
            <p className="mt-3 text-ink-soft">Share floor plans, seat count, programme type and project stage. An acoustic-first review before the shell is poured.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href={`mailto:arif@arifkhanglobal.com?subject=${encodeURIComponent("Auditorium AV Consultant — Delhi NCR")}`} className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground hover:shadow-elevated transition-shadow">
              Email Your Requirement <ArrowRight className="h-4 w-4" />
            </a>
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full border border-border-strong bg-background px-7 py-3.5 text-sm font-medium hover:border-primary hover:text-primary transition-colors">
              Let's Talk
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
